import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded'
import DomainRoundedIcon from '@mui/icons-material/DomainRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import {
  Box,
  Dialog,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  TextField,
  Typography,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { companiesApi } from '@/features/organization/api'
import { sourcesApi } from '@/features/dependencies/api'
import { usersApi } from '@/features/users/api'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { navItems } from '@/routes/navigation'
import { navItemsV2 } from '@/v2/layouts/navigation'
import { paths } from '@/routes/paths'

interface GlobalSearchProps {
  open: boolean
  onClose: () => void
  /**
   * UX-8 (P2-1, GOVIA_UX8_P2_DECISIONS.md) — `v2` = montado dentro del
   * shell cliente `AppShellV2`. Cambia la sección "Navegación" a
   * `navItemsV2` (las 7 pantallas cliente, no los módulos V1) y enruta
   * los resultados de usuarios/empresas/fuentes a `/configuracion`
   * (donde el cliente los administra en V2) en vez de a `/dependencias`
   * (página legacy V1). Objetivo: ningún resultado saca al cliente del
   * shell V2 hacia V1. Sin `v2` el comportamiento V1 queda intacto.
   */
  v2?: boolean
}

/** Se monta solo mientras el diálogo está abierto, así el estado de búsqueda nace limpio cada vez. */
function GlobalSearchBody({ onClose, v2 = false }: { onClose: () => void; v2?: boolean }) {
  const navigate = useNavigate()
  const navSource = v2 ? navItemsV2 : navItems
  const usersDest = v2 ? `${paths.settings}?tab=usuarios` : paths.users
  const companiesDest = v2 ? paths.settings : paths.dependencies
  const sourcesDest = v2 ? `${paths.settings}?tab=fuentes` : paths.dependencies
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query, 250)

  const usersQuery = useQuery({
    queryKey: ['search', 'users', debouncedQuery],
    queryFn: () => usersApi.list({ q: debouncedQuery, offset: 0, limit: 5 }),
    enabled: debouncedQuery.length >= 2,
  })

  // Cargados una vez y filtrados en cliente: estos endpoints no ofrecen búsqueda por texto en el backend.
  const companiesQuery = useQuery({
    queryKey: ['search', 'companies-all'],
    queryFn: () => companiesApi.list({ offset: 0, limit: 100 }),
    staleTime: 60_000,
  })
  const sourcesQuery = useQuery({
    queryKey: ['search', 'sources-all'],
    queryFn: () => sourcesApi.list({ offset: 0, limit: 100 }),
    staleTime: 60_000,
  })

  const matchedNav = useMemo(() => {
    if (!debouncedQuery) return navSource
    const q = debouncedQuery.toLowerCase()
    return navSource.filter((item) => item.label.toLowerCase().includes(q))
  }, [debouncedQuery, navSource])

  const matchedCompanies = useMemo(() => {
    if (debouncedQuery.length < 2) return []
    const q = debouncedQuery.toLowerCase()
    return (companiesQuery.data?.items ?? []).filter((c) => c.name.toLowerCase().includes(q)).slice(0, 5)
  }, [debouncedQuery, companiesQuery.data])

  const matchedSources = useMemo(() => {
    if (debouncedQuery.length < 2) return []
    const q = debouncedQuery.toLowerCase()
    return (sourcesQuery.data?.items ?? []).filter((s) => s.name.toLowerCase().includes(q)).slice(0, 5)
  }, [debouncedQuery, sourcesQuery.data])

  const go = (path: string) => {
    navigate(path)
    onClose()
  }

  const hasResults =
    matchedNav.length > 0 ||
    (usersQuery.data?.items.length ?? 0) > 0 ||
    matchedCompanies.length > 0 ||
    matchedSources.length > 0

  return (
    <>
      <Box sx={{ p: 1.5, borderBottom: '1px solid rgba(148,163,184,0.12)' }}>
        <TextField
          autoFocus
          fullWidth
          placeholder={v2 ? 'Buscar en GOVIA: pantallas, usuarios, fuentes…' : 'Buscar módulos, usuarios, dependencias, fuentes…'}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
        {matchedNav.length > 0 && (
          <List subheader={<ListSubheader disableSticky>Navegación</ListSubheader>} dense>
            {matchedNav.map((item) => {
              const Icon = item.icon
              return (
                <ListItemButton key={item.path} onClick={() => go(item.path)}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              )
            })}
          </List>
        )}

        {(usersQuery.data?.items.length ?? 0) > 0 && (
          <List subheader={<ListSubheader disableSticky>Usuarios</ListSubheader>} dense>
            {usersQuery.data?.items.map((user) => (
              <ListItemButton key={user.id} onClick={() => go(usersDest)}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <PersonRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={user.full_name} secondary={user.email} />
              </ListItemButton>
            ))}
          </List>
        )}

        {matchedCompanies.length > 0 && (
          <List subheader={<ListSubheader disableSticky>Dependencias</ListSubheader>} dense>
            {matchedCompanies.map((company) => (
              <ListItemButton key={company.id} onClick={() => go(companiesDest)}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <DomainRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={company.name} secondary={company.industry ?? undefined} />
              </ListItemButton>
            ))}
          </List>
        )}

        {matchedSources.length > 0 && (
          <List subheader={<ListSubheader disableSticky>Fuentes de datos</ListSubheader>} dense>
            {matchedSources.map((source) => (
              <ListItemButton key={source.id} onClick={() => go(sourcesDest)}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <BusinessRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={source.name} secondary={source.type.toUpperCase()} />
              </ListItemButton>
            ))}
          </List>
        )}

        {!hasResults && debouncedQuery.length >= 2 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            Sin resultados para "{debouncedQuery}".
          </Typography>
        )}
      </Box>
    </>
  )
}

export function GlobalSearch({ open, onClose, v2 = false }: GlobalSearchProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { position: 'fixed', top: 96, m: 0 } } }}
    >
      {open && <GlobalSearchBody onClose={onClose} v2={v2} />}
    </Dialog>
  )
}
